import React from 'react';
import { PhioengagePatientProfilev2 } from '../types';
import { useSnackbar } from 'notistack';
import { firestoreInstance } from 'config/ApiService';
import { doc, getDoc } from 'firebase/firestore';
import { LoadingButton } from '@mui/lab';
import { makeStyles } from '@mui/styles';
import { IconAlertTriangleFilled } from '@tabler/icons-react';
import {
    Backdrop,
    Box,
    Dialog,
    DialogContent,
    DialogTitle,
    FormControl,
    InputLabel,
    MenuItem,
    Select,
    Stack,
    TextField,
    Tooltip,
    Typography,
    CircularProgress
} from '@mui/material';
import useConfirm from 'hooks/useConfirm';
import DefaultDischargeConfig from '../data/DefaultDischargeConfig';
import CaseHandler from '../backend/CaseHandler';
import { useEngageStore } from '../state';
import { setUserActivity } from 'services/trackUserActivity';

const useStyles = makeStyles(() => ({
    formControl: {
        width: '300px'
    }
}));

export const DischargeOptions = [
    { id: 'failed_to_onboard', name: 'Failed to Onboard' },
    { id: 'episode_closed', name: 'Episode Closed' },
    { id: 'episode_complete', name: 'Episode Complete' },
    { id: 'onward_referral', name: 'Onward Referral' },
    { id: 'other', name: 'Other' }
];

const DischargeForm = ({
    profile,
    onDismiss
}: {
    profile: PhioengagePatientProfilev2 | null;
    onDismiss: () => void;
}) => {
    const [state, setState] = React.useState({
        dischargeReason: '',
        treatmentNote: '',
        emailBody: '',
        patientMessage: '',
        customerNote: '',
        appOverlayHeader: '',
        appOverlayMessage: '',
        emailSubject: ''
    });
    const [discharging, setDischarging] = React.useState(false);
    const classes = useStyles();
    const { enqueueSnackbar } = useSnackbar();
    const { setThreadId } = useEngageStore();

    const handleOnInputChange = (val: string) => {
        setState((prev) => ({
            ...prev,
            dischargeReason: val
        }));
    };

    const clearState = () => {
        setState({
            dischargeReason: '',
            treatmentNote: '',
            emailBody: '',
            patientMessage: '',
            customerNote: '',
            appOverlayHeader: '',
            appOverlayMessage: '',
            emailSubject: ''
        });
        setThreadId('');
    };

    const dischargeReasonEmpty = state.dischargeReason.trim() === '';

    const handlePatientDischarge = async (user: PhioengagePatientProfilev2 | null) => {
        if (!user) {
            return;
        }
        // validate all fields
        if (
            !state.dischargeReason ||
            !state.treatmentNote ||
            !state.emailBody ||
            !state.patientMessage
        ) {
            enqueueSnackbar('Please fill in all required fields', { variant: 'error' });
            return;
        }
        setDischarging(true);
        const handler = new CaseHandler(user.region);
        try {
            await handler.dischargePatient({
                userId: profile?.id ?? '',
                region: user.region,
                dischargeReason: state.dischargeReason,
                treatmentNote: state.treatmentNote,
                emailSubject: state.emailSubject,
                emailBody: state.emailBody,
                patientMessage: state.patientMessage,
                customerNote: state.customerNote,
                appOverlayHeader: state.appOverlayHeader,
                appOverlayMessage: state.appOverlayMessage
            });
            setUserActivity({
                patientId: profile?.id,
                eventName: 'patient_discharged'
            });
            enqueueSnackbar('User discharged successfully', { variant: 'success' });
            // clear state
            clearState();
            // dismiss the dialog
            onDismiss();
        } catch (e) {
            console.error('Error discharging user:', e);
            enqueueSnackbar('Failed to discharge user', { variant: 'error' });
        } finally {
            setDischarging(false);
        }
    };

    React.useEffect(() => {
        (async () => {
            if (!profile || !profile.id || !state.dischargeReason) {
                return;
            }
            const ref = doc(
                firestoreInstance(),
                'clients',
                profile.customer_id,
                'discharge_configs',
                state.dischargeReason
            );
            const snapshot = await getDoc(ref);
            if (!snapshot.exists()) {
                // get the defaults
                const defaultConfig = DefaultDischargeConfig[state.dischargeReason];
                if (defaultConfig) {
                    setState((prev) => ({
                        ...prev,
                        treatmentNote: defaultConfig.treatment_note ?? '',
                        emailBody: defaultConfig.patient_email_body ?? '',
                        customerNote: defaultConfig.customer_note ?? '',
                        patientMessage: defaultConfig.app_overlay_message ?? '',
                        appOverlayHeader: defaultConfig.app_overlay_header ?? '',
                        appOverlayMessage: defaultConfig.app_overlay_message ?? '',
                        emailSubject: defaultConfig.patient_email_subject ?? ''
                    }));
                    enqueueSnackbar('Using default discharge configuration for this reason', {
                        variant: 'info'
                    });
                    return;
                }
            }
            const data = snapshot.data();
            if (!data) {
                return;
            }
            setState((prev) => ({
                ...prev,
                treatmentNote: data.treatment_note ?? '',
                emailBody: data.patient_email_body ?? '',
                customerNote: data.customer_note ?? '',
                patientMessage: data.app_overlay_message ?? '',
                appOverlayHeader: data.app_overlay_header ?? '',
                appOverlayMessage: data.app_overlay_message ?? '',
                emailSubject: data.patient_email_subject ?? ''
            }));
        })();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [profile, state.dischargeReason]);

    return (
        <Stack spacing={1} sx={{ padding: 1 }}>
            <Box>
                <FormControl
                    // fullWidth={fullWidth}
                    variant="outlined"
                    className={classes.formControl}
                    error={!state.dischargeReason}
                    required
                >
                    <InputLabel id="discharge-reason-label">Discharge reason</InputLabel>
                    <Select
                        labelId="discharge-reason-label"
                        value={state.dischargeReason}
                        onChange={(evt) => handleOnInputChange(evt.target.value)}
                        id="discharge-reason-select-outlined"
                        label={'Discharge reason'}
                        required
                    >
                        {DischargeOptions.map((option) => (
                            <MenuItem key={option.id} value={option.id}>
                                {option.name}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>
                <Box sx={{ flex: 1 }} />
            </Box>
            <Box>
                <TextField
                    required
                    fullWidth
                    variant="outlined"
                    label="App Overlay Message"
                    rows={3}
                    multiline
                    error={!state.patientMessage}
                    value={state.patientMessage}
                    onChange={(evt) =>
                        setState((prev) => ({
                            ...prev,
                            patientMessage: evt.target.value
                        }))
                    }
                    disabled={dischargeReasonEmpty}
                />
            </Box>
            <Box>
                <TextField
                    required
                    fullWidth
                    variant="outlined"
                    label="Customer Note"
                    rows={3}
                    multiline
                    error={!state.customerNote}
                    value={state.customerNote}
                    onChange={(evt) =>
                        setState((prev) => ({
                            ...prev,
                            customerNote: evt.target.value
                        }))
                    }
                    disabled={dischargeReasonEmpty}
                />
            </Box>
            <Box>
                <TextField
                    fullWidth
                    required
                    variant="outlined"
                    label="Treatment note"
                    multiline
                    rows={2}
                    value={state.treatmentNote ?? ''}
                    onChange={(evt) =>
                        setState((prev) => ({
                            ...prev,
                            treatmentNote: evt.target.value
                        }))
                    }
                    error={!state.treatmentNote}
                    disabled={dischargeReasonEmpty}
                />
            </Box>
            <Box>
                <TextField
                    fullWidth
                    required
                    variant="outlined"
                    label="Email body"
                    multiline
                    rows={2}
                    value={state.emailBody ?? ''}
                    onChange={(evt) =>
                        setState((prev) => ({
                            ...prev,
                            emailBody: evt.target.value
                        }))
                    }
                    error={!state.emailBody}
                    disabled={dischargeReasonEmpty}
                />
            </Box>
            <Stack direction="row" spacing={1} sx={{ marginTop: 1 }} justifyContent={'flex-end'}>
                <LoadingButton
                    variant="outlined"
                    color="secondary"
                    onClick={() => {
                        // clear state
                        clearState();
                        onDismiss();
                    }}
                >
                    Cancel
                </LoadingButton>
                <LoadingButton
                    variant="contained"
                    color="primary"
                    onClick={() => {
                        // Handle submit logic here
                        handlePatientDischarge(profile);
                    }}
                    loading={discharging}
                    disabled={
                        !state.dischargeReason ||
                        !state.treatmentNote ||
                        !state.emailBody ||
                        !state.patientMessage
                    }
                >
                    Submit
                </LoadingButton>
            </Stack>
        </Stack>
    );
};

/**
 * Discharge Button
 * This component encapsulates the discharge button functionality
 */
const DischargeButton = ({ profile }: { profile: PhioengagePatientProfilev2 | null }) => {
    const [changingStatus, setChangingStatus] = React.useState(false);
    const [openDialog, setOpenDialog] = React.useState(false);
    const { enqueueSnackbar } = useSnackbar();

    const pendingTasks = profile?.has_open_tasks || profile?.has_open_chat_thread;

    const { isConfirmed } = useConfirm();

    const handleOpenDischargeDialog = async (operation: 'discharge' | 'reinstate') => {
        const confirm = await isConfirmed(
            `Are you sure you want to ${operation} this user?`,
            'Confirm'
        );
        if (!confirm) return;
        setOpenDialog(true);
    };

    const handleCaseReinstate = async (user: PhioengagePatientProfilev2 | null) => {
        const confirm = await isConfirmed(
            'Are you sure you want to reinstate this patient? This will reopen the record as a new episode of care and remove the discharge status.',
            'Confirm'
        );
        if (!confirm) return;
        try {
            setChangingStatus(true);
            const handler = new CaseHandler(profile?.region ?? '');
            await handler.reinstateCase(profile?.id ?? '');
            setUserActivity({
                patientId: profile?.id,
                eventName: 'programme_reinstated'
            });
            enqueueSnackbar('User has been reinstated successfully', { variant: 'success' });
        } catch (e) {
            console.error('Error reinstating user:', e);
            enqueueSnackbar('Failed to reinstate user', { variant: 'error' });
        } finally {
            setChangingStatus(false);
        }
    };

    return (
        <>
            {profile?.case_status !== 'discharged' ? (
                <Tooltip
                    title={
                        pendingTasks
                            ? 'Discharge is unavailable while open actions exist on this case.'
                            : 'Discharge this patient from their programme'
                    }
                >
                    <Box>
                        <LoadingButton
                            size="small"
                            sx={{ textTransform: 'none' }}
                            loading={changingStatus}
                            disabled={pendingTasks}
                            onClick={() => handleOpenDischargeDialog('discharge')}
                        >
                            <IconAlertTriangleFilled size={24} />
                            <Typography sx={{ marginLeft: 1 }}>Discharge</Typography>
                        </LoadingButton>
                    </Box>
                </Tooltip>
            ) : (
                <Tooltip title="Reinstate this patient to their programme">
                    <LoadingButton
                        size="small"
                        sx={{ textTransform: 'none' }}
                        loading={changingStatus}
                        onClick={() => handleCaseReinstate(profile)}
                    >
                        <IconAlertTriangleFilled size={24} />
                        <Typography sx={{ marginLeft: 1 }}>Reinstate</Typography>
                    </LoadingButton>
                </Tooltip>
            )}
            <Dialog open={openDialog} maxWidth="md" fullWidth keepMounted>
                <DialogTitle>Discharge</DialogTitle>
                <DialogContent sx={{ padding: 0 }}>
                    <DischargeForm profile={profile} onDismiss={() => setOpenDialog(false)} />
                </DialogContent>
            </Dialog>
            <Backdrop
                sx={{
                    zIndex: (theme) => theme.zIndex.drawer + 1,
                    color: '#fff',
                    backgroundColor: 'rgba(0, 0, 0, 0.8)'
                }}
                open={changingStatus}
            >
                <Box textAlign="center">
                    <CircularProgress color="inherit" />
                    <Typography variant="h4" component="p">
                        Reinstating patient data. Please wait…
                    </Typography>
                </Box>
            </Backdrop>
        </>
    );
};

export default DischargeButton;
